'use client';

import { useEffect, useCallback, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import type { FredRange } from '@/lib/fred-series';
import { RangeSelector } from './RangeSelector';
import {
  TimeSeriesChart,
  prepareSeries,
  formatDateTickDetailed,
  formatValue,
  type RecessionRange,
} from './TimeSeriesChart';

interface SeriesPayload {
  observations: { date: string; value: number | null }[];
  lastUpdated?: string;
}

interface ChartExpandModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description: string;
  color: string;
  unit: string;
  chartType: 'line' | 'bar';
  seriesLabel: string;
  initialRange: FredRange;
  recessionRanges: RecessionRange[];
  /** React Query key builder for the selected range. */
  queryKey: (range: FredRange) => unknown[];
  /** URL to fetch observations for the selected range. */
  fetchUrl: (range: FredRange) => string;
  /** Optional custom value formatter (e.g. Shiller PE has no unit suffix). */
  valueFormatter?: (v: number) => string;
}

export function ChartExpandModal({
  isOpen,
  onClose,
  title,
  description,
  color,
  unit,
  chartType,
  seriesLabel,
  initialRange,
  recessionRanges,
  queryKey,
  fetchUrl,
  valueFormatter,
}: ChartExpandModalProps) {
  const [range, setRange] = useState<FredRange>(initialRange);
  const [hover, setHover] = useState<{ dateMs: number; value: number } | null>(null);

  // Re-sync to the tab's range each time the modal transitions to open. Using the
  // "adjust state during render" pattern (tracking the previous open state) instead
  // of an effect avoids a cascading re-render.
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (isOpen) {
      setRange(initialRange);
      setHover(null);
    }
  }

  const handleEscape = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose],
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, handleEscape]);

  const { data, isLoading, isError, refetch } = useQuery<SeriesPayload>({
    queryKey: queryKey(range),
    queryFn: async () => {
      const res = await fetch(fetchUrl(range));
      if (!res.ok) throw new Error('Failed to fetch series');
      return res.json();
    },
    staleTime: 12 * 60 * 60 * 1000,
    retry: 1,
    enabled: isOpen,
  });

  const prepared = useMemo(() => prepareSeries(data?.observations), [data?.observations]);
  const fmtValue = valueFormatter ?? ((v: number) => formatValue(v, unit));

  const stats = useMemo(() => computeStats(prepared.observations), [prepared.observations]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto overflow-x-hidden bg-black/60 backdrop-blur-md p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.97, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.97, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="relative w-full max-w-5xl bg-[#0F1525] border border-[#1F2937] rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-start justify-between gap-4 p-4 border-b border-[#1F2937]">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-[var(--color-accent)] uppercase tracking-wider">
                  {title}
                </div>
                <div className="text-[11px] text-[#6B7280] mt-1">{description}</div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <RangeSelector value={range} onChange={setRange} />
                <button
                  onClick={onClose}
                  aria-label="Close"
                  className="rounded p-1 text-[#6B7280] hover:text-[#E5E7EB] hover:bg-[#1F2937] transition-colors"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Stats / hover readout */}
            <div className="flex flex-wrap items-center gap-x-8 gap-y-2 px-4 pt-4 font-mono">
              <Stat
                label={hover ? 'Selected' : 'Latest'}
                primary={hover ? fmtValue(hover.value) : stats ? fmtValue(stats.last) : '—'}
                secondary={
                  hover
                    ? formatDateTickDetailed(hover.dateMs)
                    : stats
                      ? formatDateTickDetailed(stats.lastMs)
                      : ''
                }
                color={color}
              />
              <Stat label="Min" primary={stats ? fmtValue(stats.min) : '—'} secondary={stats ? formatDateTickDetailed(stats.minMs) : ''} />
              <Stat label="Max" primary={stats ? fmtValue(stats.max) : '—'} secondary={stats ? formatDateTickDetailed(stats.maxMs) : ''} />
              {stats && (
                <Stat
                  label="Change"
                  primary={`${stats.change >= 0 ? '+' : ''}${fmtValue(stats.change)}`}
                  secondary="over range"
                  color={stats.change >= 0 ? '#22C55E' : '#EF4444'}
                />
              )}
            </div>

            {/* Chart */}
            <div className="p-4">
              {isLoading ? (
                <div className="w-full bg-[#1F2937] animate-pulse rounded" style={{ height: 460 }} />
              ) : isError || !prepared.hasData ? (
                <div
                  className="w-full flex items-center justify-center text-xs text-[#6B7280] border border-dashed border-[#1F2937]"
                  style={{ height: 460 }}
                >
                  <div className="text-center">
                    <div>{isError ? 'Data unavailable' : 'No data'}</div>
                    {isError && (
                      <button
                        onClick={() => refetch()}
                        className="mt-2 px-2 py-0.5 text-[10px] text-[var(--color-accent)] hover:underline"
                      >
                        Retry
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <TimeSeriesChart
                  prepared={prepared}
                  recessionRanges={recessionRanges}
                  color={color}
                  unit={unit}
                  chartType={chartType}
                  seriesLabel={seriesLabel}
                  height={460}
                  detailed
                  valueFormatter={fmtValue}
                  onHover={setHover}
                />
              )}
              <div className="mt-2 text-[10px] text-[#4B5563] font-mono">
                Hover the chart to read the exact value on a given date · gray bands = NBER recessions
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Stat({
  label,
  primary,
  secondary,
  color,
}: {
  label: string;
  primary: string;
  secondary: string;
  color?: string;
}) {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] uppercase tracking-wider text-[#6B7280]">{label}</span>
      <span className="text-sm font-semibold" style={{ color: color ?? '#E5E7EB' }}>
        {primary}
      </span>
      <span className="text-[10px] text-[#6B7280]">{secondary}</span>
    </div>
  );
}

interface SeriesStats {
  last: number;
  lastMs: number;
  min: number;
  minMs: number;
  max: number;
  maxMs: number;
  change: number;
}

function computeStats(
  observations: { value: number | null; dateMs: number }[],
): SeriesStats | null {
  let first: { value: number; dateMs: number } | null = null;
  let last: { value: number; dateMs: number } | null = null;
  let min: { value: number; dateMs: number } | null = null;
  let max: { value: number; dateMs: number } | null = null;
  for (const o of observations) {
    if (o.value === null) continue;
    const point = { value: o.value, dateMs: o.dateMs };
    if (first === null) first = point;
    last = point;
    if (min === null || o.value < min.value) min = point;
    if (max === null || o.value > max.value) max = point;
  }
  if (!first || !last || !min || !max) return null;
  return {
    last: last.value,
    lastMs: last.dateMs,
    min: min.value,
    minMs: min.dateMs,
    max: max.value,
    maxMs: max.dateMs,
    change: last.value - first.value,
  };
}
